import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Headphones, Plus, RefreshCw, AlertCircle, X } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useSupportTickets } from "@/providers/SupportTicketsProvider";
import { TicketCard } from "./TicketCard";
import { CreateTicketDialog } from "./CreateTicketDialog";
import type { SupportTicketDto } from "@shared/types/SupportTicketDto";

export function SupportPage() {
  const navigate = useNavigate();
  const { fetchTickets } = useSupportTickets();
  const [fetchTicketsError, setFetchTicketsError] = useState<string | null>(
    null,
  );
  const [tickets, setTickets] = useState<SupportTicketDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const safeTickets = tickets ?? [];
  const hasTickets = safeTickets.length > 0;
  const openTickets = safeTickets.filter((t) => !t.isResolved);
  const resolvedTickets = safeTickets.filter((t) => t.isResolved);

  function handleTicketCreated(ticket: SupportTicketDto) {
    setCreateDialogOpen(false);
    navigate(`/support/${ticket.ticketId}`);
  }

  function refreshTickets() {
    setIsLoading(true);
    setFetchTicketsError(null);
    fetchTickets()
      .then((tickets) => {
        setTickets(tickets);
      })
      .catch((error) => {
        setFetchTicketsError(
          error instanceof Error ? error.message : "Failed to fetch tickets",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  function renderErrorBanner() {
    if (!fetchTicketsError) return null;

    return (
      <Card className="mb-6 border-red-500/50 bg-red-500/10">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <span className="text-sm text-red-200">{fetchTicketsError}</span>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={refreshTickets}
                className="h-8 text-xs border-red-500/50 hover:bg-red-500/20"
              >
                Try Again
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setFetchTicketsError(null)}
                className="h-8 w-8 hover:bg-red-500/20"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  function renderLoadingState() {
    return (
      <Card className="glass-card border-gradient">
        <CardContent className="p-12 text-center">
          <RefreshCw className="h-8 w-8 mx-auto animate-spin text-muted-foreground" />
          <p className="text-muted-foreground mt-4">Loading tickets...</p>
        </CardContent>
      </Card>
    );
  }

  function renderEmptyState() {
    return (
      <Card className="glass-card border-gradient">
        <CardContent className="p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Headphones className="h-8 w-8 text-primary" />
          </div>
          <h3 className="font-semibold text-lg mb-2">No Support Tickets</h3>
          <p className="text-muted-foreground mb-6">
            Need help with a project? Create a support ticket and our team will
            assist you.
          </p>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Your First Ticket
          </Button>
        </CardContent>
      </Card>
    );
  }

  function renderTicketsList() {
    return (
      <div className="space-y-8">
        {openTickets.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-amber-400">
              Open Tickets ({openTickets.length})
            </h2>
            {openTickets.map((ticket, index) => (
              <motion.div
                key={ticket.ticketId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <TicketCard
                  ticket={ticket}
                  onClick={() =>
                    navigate(`/support/${ticket.ticketId}`)
                  }
                />
              </motion.div>
            ))}
          </div>
        )}

        {resolvedTickets.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-muted-foreground">
              Resolved Tickets ({resolvedTickets.length})
            </h2>
            {resolvedTickets.map((ticket, index) => (
              <motion.div
                key={ticket.ticketId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <TicketCard
                  ticket={ticket}
                  onClick={() => navigate(`/support/${ticket.ticketId}`)}
                />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    );
  }

  function renderContent() {
    if (isLoading && !hasTickets) {
      return renderLoadingState();
    }

    if (!hasTickets) {
      return renderEmptyState();
    }

    return renderTicketsList();
  }

  useEffect(() => {
    if (!isLoading && tickets.length === 0) {
      refreshTickets();
    }
  }, [refreshTickets, isLoading, tickets]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold mb-2">Support</h1>
          <p className="text-muted-foreground">
            Get help with your projects and report issues.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={refreshTickets}>
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
          </Button>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create a Ticket
          </Button>
        </div>
      </div>

      {renderErrorBanner()}
      {renderContent()}

      <CreateTicketDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onTicketCreated={handleTicketCreated}
      />
    </motion.div>
  );
}
